import type { ReactElement } from 'react';
import { Button } from './Button';
import { SupportLink } from './SupportLink';

export interface AppErrorFallbackProps {
  /** Reset callback supplied by `<Sentry.ErrorBoundary>`. Optional for test isolation. */
  onReset?: () => void;
}

/**
 * Root error fallback rendered by `<Sentry.ErrorBoundary>` in App.tsx when a
 * render-time React error escapes a route component (per ADR 0001 + ADR 0006).
 *
 * Authored from scratch per ADR 0006 (NOT extending `<EmptyState>`) — the
 * title requires `text-text-bright`, which differs from EmptyState's default
 * `text-text-muted`. Composes Panel visual tokens via the outer div class list;
 * the `<Panel>` primitive was not used directly because it mandates header
 * chrome (title bar) that would add unwanted visual noise on a full-screen
 * error surface.
 */
export function AppErrorFallback({
  onReset,
}: AppErrorFallbackProps): ReactElement {
  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-6">
      {/* Panel chrome: bg-void + panel-border + 3px radius — mirrors Panel primitive tokens */}
      <div className="bg-void border border-panel-border rounded-[3px] max-w-md w-full p-8 flex flex-col items-center gap-6 text-center">
        <h2 className="text-sm font-mono font-semibold text-text-bright tracking-[1px] uppercase">
          Something Went Wrong
        </h2>
        <p className="text-sm font-body text-text-muted">
          An unexpected error occurred. Refreshing the page usually resolves it.
        </p>
        <div className="flex gap-3">
          <Button variant="primary" onClick={() => window.location.reload()}>
            Reload page
          </Button>
          <Button variant="ghost" onClick={onReset} disabled={!onReset}>
            Try again
          </Button>
        </div>
        {/* Support contact — appears unconditionally on the root error
            fallback so users have a path out when both Reload and Try
            again leave them stuck. */}
        <SupportLink variant="inline" subject="Error on Arcanum web app" />
      </div>
    </div>
  );
}
